import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, Send, Sparkles, User, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAME = "Hematopath AI";

const CHIPS = [
  "Why should we hire Dr. Rahul?",
  "Walk me through his health-tech products",
  "Consulting terms & availability",
  "Brand collaborations & audience reach",
  "Summarise his publications",
];

const getSessionId = () => {
  let id = localStorage.getItem("hematopath-ai-session");
  if (!id) {
    id = `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("hematopath-ai-session", id);
  }
  return id;
};

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Namaste — I'm Hematopath AI, Dr. Rahul's on-site twin. Ask me about his gold medal, his products — e-parchi, Nirogyam AI, Occusafe, LabTrack — his publications, quality work, consulting terms — anything on the record.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(getSessionId());

  useEffect(() => {
    const openFn = () => setOpen(true);
    window.addEventListener("open-ai-chat", openFn);
    return () => window.removeEventListener("open-ai-chat", openFn);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typing]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setBusy(true);
    setTyping(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");

      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let first = true;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line || line === "data: [DONE]") continue;
          try {
            const payload = JSON.parse(line);
            if (payload.error) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", text: payload.error };
                return c;
              });
              setTyping(false);
              setBusy(false);
              return;
            }
            if (payload.delta) {
              if (first) setTyping(false);
              first = false;
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + payload.delta };
                return c;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", text: "Connection hiccup — please try that question again." };
        return c;
      });
    } finally {
      setTyping(false);
      setBusy(false);
    }
  };

  return (
    <>
      {/* Trigger */}
      <motion.button
        data-testid="ai-chat-trigger-button"
        onClick={() => setOpen((o) => !o)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.6, type: "spring", stiffness: 260, damping: 20 }}
        whileHover={{ scale: 1.06 }}
        className="fixed bottom-6 right-6 z-[60] group flex items-center gap-2.5 rounded-full pl-4 pr-5 py-3.5 bg-gradient-to-r from-neon to-vio text-ink font-semibold text-sm shadow-glow-cyan"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ink/50 opacity-70" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-ink" />
        </span>
        <Sparkles size={17} />
        {open ? "Close" : "Ask my AI"}
      </motion.button>

      {/* Drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-drawer"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[420px] h-[560px] max-h-[70vh] glass rounded-2xl overflow-hidden flex flex-col shadow-glow-vio"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.07] bg-white/[0.02]">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-tr from-neon/20 to-vio/20 border border-white/10">
                  <Bot size={17} className="text-neon" />
                </span>
                <div>
                  <p className="font-display text-sm font-semibold">{NAME}</p>
                  <p className="text-[10px] font-mono text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <span className="h-1 w-1 rounded-full bg-emerald-400" /> Streaming · GPT-powered
                  </p>
                </div>
              </div>
              <button data-testid="ai-chat-close-button" onClick={() => setOpen(false)} className="text-slate-500 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-lenis-prevent className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="ai-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`chat-message-${m.role}`}>
                  <span className={`grid place-items-center w-7 h-7 rounded-lg shrink-0 mt-0.5 ${m.role === "user" ? "bg-white/[0.06] border border-white/10" : "bg-gradient-to-tr from-neon/20 to-vio/20 border border-white/10"}`}>
                    {m.role === "user" ? <User size={13} className="text-slate-300" /> : <Bot size={13} className="text-neon" />}
                  </span>
                  <div className={`max-w-[80%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-white/[0.07] text-slate-100 rounded-tr-sm" : "bg-gradient-to-br from-neon/[0.08] to-vio/[0.08] border border-white/[0.06] text-slate-200 rounded-tl-sm"}`}>
                    {m.text}
                    {m.role === "assistant" && !m.text && typing && (
                      <span className="inline-flex gap-1 pl-1 pt-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-neon animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="h-1.5 w-1.5 rounded-full bg-vio animate-bounce" style={{ animationDelay: "120ms" }} />
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "240ms" }} />
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Chips */}
            <div className="px-4 pb-2 flex gap-2 overflow-x-auto" data-testid="ai-chat-chips">
              {CHIPS.map((c) => (
                <button
                  key={c}
                  data-testid="ai-chat-chip"
                  onClick={() => send(c)}
                  disabled={busy}
                  className="shrink-0 text-[11px] text-slate-300 border border-white/12 rounded-full px-3 py-1.5 bg-white/[0.03] hover:border-neon/50 hover:text-white transition-colors duration-300 disabled:opacity-50"
                >
                  {c}
                </button>
              ))}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/[0.07] flex items-center gap-2">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about roles, projects, terms…"
                className="flex-1 rounded-full bg-white/[0.05] border border-white/10 px-4 py-2.5 text-sm placeholder:text-slate-600 focus:outline-none focus:border-neon/50 transition-colors"
              />
              <button
                data-testid="ai-chat-send-button"
                onClick={() => send()}
                disabled={busy || !input.trim()}
                className="grid place-items-center w-10 h-10 rounded-full bg-gradient-to-r from-neon to-vio text-ink disabled:opacity-40 hover:shadow-glow-cyan transition-shadow duration-300"
              >
                <Send size={15} />
              </button>
            </div>
            <p className="pb-2.5 text-center text-[10px] font-mono text-slate-600">
              AI-generated from Dr. Rahul's verified CV · verify at hi@rahulkanungo.com
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}